import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/services/dialog.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import Swal from 'sweetalert2';
import { DocumentTemplateService as ComponentService } from './document-template.service';
import { AddDocumentTemplateComponent as AddComponent } from './add-document-template/add-document-template.component';
import { EditDocumentTemplateComponent as EditComponent } from './edit-document-template/edit-document-template.component';
import { helper } from 'src/environments/helper';

import { DocumentTemplateSignaturesComponent } from './document-template-signatures/document-template-signatures.component';
import { CloneTemplateDialogComponent } from './clone-template-dialog/clone-template-dialog.component';
import { SmartTemplateBuilderComponent } from './smart-template-builder/smart-template-builder.component';
import { SmartTemplateAssignmentComponent } from './smart-template-assignment/smart-template-assignment.component';
import { SmartTemplateManagementComponent } from './smart-template-management/smart-template-management.component';
import { TemplateParameterComponent } from './template-parameter/template-parameter.component';
import { PermissionsService } from '../../../services/permissions.service';
import { FormControl, FormGroup } from '@angular/forms';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { AppFilesService } from 'src/app/services/app-files.service';

@Component({
    selector: 'app-document-template',
    templateUrl: './document-template.component.html',
    styleUrls: ['./document-template.component.scss'],
})
export class DocumentTemplateComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    activeValue: string = '-1';
    publicValue: string = '-1';

    directii: any[] = [];
    compartimente!: Generic[];

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredDirectii!: any[];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();
    registriesList: any[] = [];
    categorySearchResults: any[] = [];
    categorySearch: string = '';
    selectedCategoryId: number | null = null;

    // Debouncing for category search
    private categorySearchTimeout: any;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink = this.componentService.getListDTUrl;

    searchParamsForm!: FormGroup;

    //Form Initial Values

    initialFormValues = {
        name: '',
        directie_id: -1,
        compartiment_id: -1,
        registry_id: -1,
        draft_status: '-1',
        address_type: '-1',
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            name: new FormControl(''),
            directie_id: new FormControl(-1),
            compartiment_id: new FormControl(-1),
            registry_id: new FormControl(-1),
            draft_status: new FormControl('-1'),
            address_type: new FormControl('-1'),
        });
    }

    parameters: { key: string; value: string }[] = [
        { key: '-- Alegeți --', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    constructor(
        private dialogService: DialogService,
        private http: HttpClient,
        private dialog: MatDialog,
        private componentService: ComponentService,
        public permissionsService: PermissionsService,
        private commonService: CommonService,
        private appFilesService: AppFilesService,
    ) { }

    ngOnInit(): void {
        this.loadDirectiiList();
        this.loadFilterOptions();
        this.renderDT();
        this.buildSearchParamsForm();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directii;
                    return of(this.directii);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimente;
                    return of(this.compartimente);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directii.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadDirectiiList(): void {
        this.isFilterLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directii = results;

                    this.filteredDirectii = [...this.directii];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectii = [];
                },
            });
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(categorie =>
                categorie.id === this.searchParamsForm.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.searchParamsForm.controls['compartiment_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimente = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isFilterLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (compartimente) => {
                this.compartimente = [...compartimente];
                this.filteredCompartimente = [...compartimente];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            }
        });
    }

    onCompartimenteSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.searchParamsForm.get('compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimente;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterCompartimente(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.compartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        ));
    }

    resetForm() {
        this.activeValue = '-1';
        this.publicValue = '-1';
        this.categorySearchResults = [];

        this.rerenderDT();
        this.showCard = true;
        this.searchParamsForm.reset(this.initialFormValues);
        this.onDirectieChange(-1);

        this.filteredDirectii = this.directii;
        this.directiiControl.setValue(null);

        this.compartimenteControl.setValue(null);

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            responsive: true,
            searching: false,
            pageLength: 25,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.active = this.activeValue;
                dataTablesParameters.public = this.publicValue;
                Object.assign(dataTablesParameters, { ...this.searchParamsForm.value });
                dataTablesParameters.category_search = this.categorySearch || null;
                dataTablesParameters.category_id = this.selectedCategoryId;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Draft',
                    data: 'name',
                    width: '400px',
                    render: (data: any, type: any, row: any) => {
                        return `<strong>Draft:</strong> ${row.name} <br> <strong>Tip Cerere:</strong> ${row.type}`
                    }
                },
                // {
                //     title: 'Denumire',
                //     data: 'name',
                //     width: '300px',
                // },
                {
                    title: 'Organizare / Categorie',
                    data: 'directie_name',
                    sortable: false,
                    width: '250px',
                    render: (data: any, type: any, row: any) => {
                        let html = '';

                        // Organization info
                        if (row.directie_name) {
                            html += `<strong>Dir:</strong> ${row.directie_name}`;
                        }
                        if (row.compartiment_name) {
                            html += `<br><strong>Comp:</strong> ${row.compartiment_name}`;
                        }

                        // Category info
                        if (row.primary_category_info) {
                            html += `<br><i class="fas fa-tag text-primary me-1"></i><strong>Cat:</strong> ${row.primary_category_info.name}`;
                        } else if (row.categories && row.categories.length > 0) {
                            html += `<br><i class="fas fa-tags text-secondary me-1"></i><small>${row.categories.length} categorii</small>`;
                        }

                        return html || '<span class="text-muted">Nespecificat</span>';
                    }
                },
                {
                    title: 'Registru',
                    data: 'registry_name',
                },
                {
                    title: 'Drafturi Diferite?',
                    data: 'document_generabil_dual',
                    render: (data: any, type: any, row: any) => {
                        return row.document_generabil_dual === true
                            ? 'Da'
                            : 'Nu';
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        return `
                        <div class="d-flex align-center justify-content-left">
              				<button class="action-btn edit ms-1">
                				<img class="btn-img" src="../../../../assets/icons/edit.svg" alt="edit"/>
              				</button>
              				<button class="action-btn delete ms-1">
                				<img class="btn-img" src="../../../../assets/icons/delete.svg" alt="delete"/>
              				</button>
                            <button type="button" class="action-btn update_status_active ms-1 ` +
                            (row.active == true ? 'action-success' : 'action-danger') +
                            `">
                                    <img class="btn-img" src="` +
                            (row.active == true
                                ? '/assets/icons/check-white.svg'
                                : '/assets/icons/close-white.svg') +
                            `" alt="update_status_active"/>
                            </button>
                            <button class="action-btn clone ms-1" title="Clonează template">
                                <img class="btn-img" src="../../../../assets/icons/double-file.svg" alt="clone"/>
                            </button>
							<button class="action-btn signatures ms-1">
                				<img class="btn-img" src="../../../../assets/icons/simple-signature-contract.svg" alt="signatures"/>
              				</button>
                        </div>
            `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const cloneIcon = lastTd?.querySelector('.clone') as HTMLElement;
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const signaturesIcon = lastTd?.querySelector(
                    '.signatures'
                ) as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;
                const updateStatusActiveIcon = lastTd?.querySelector(
                    '.update_status_active'
                ) as HTMLElement;

                if (cloneIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'dgaspc-module.settings.template-documente.add'
                        )
                    ) {
                        cloneIcon.style.display = 'none';
                    }
                    cloneIcon.addEventListener('click', () => {
                        this.openCloneDialog(data);
                    });
                }

                if (signaturesIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'dgaspc-module.settings.template-documente.actualizare-semnatari'
                        )
                    ) {
                        signaturesIcon.style.display = 'none';
                    }
                    signaturesIcon.addEventListener('click', () => {
                        this.openSignaturesDialog(data);
                    });
                }

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'dgaspc-module.settings.template-documente.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'dgaspc-module.settings.template-documente.delete'
                        )
                    ) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.delete(id);
                    });
                }

                if (updateStatusActiveIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'dgaspc-module.settings.template-documente.update'
                        )
                    ) {
                        updateStatusActiveIcon.style.display = 'none';
                    }
                    updateStatusActiveIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.changeStatusActive(id);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });
        this.showCard = false;
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddComponent, {
            height: '80vh',
            width: '70vw',
            autoFocus: false,
            data: this.directii
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare adaugată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditDialog(data: any) {
        const dialogRef = this.dialog.open(EditComponent, {
            height: '80vh',
            width: '70vw',
            data: {
                data: data,
                directiiActive: this.directii,
            },
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openSignaturesDialog(data: any) {
        const dialogRef = this.dialog.open(DocumentTemplateSignaturesComponent, {
            height: '70vh',
            maxHeight: '70vh',
            width: '75vh',
            maxWidth: '75vh',
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    delete(id: number) {
        Swal.fire({
            title: 'Sunteți sigur?',
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Confirm!',
            cancelButtonText: 'Cancel',
            showLoaderOnConfirm: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.componentService.delete(id).subscribe(
                    () => {
                        this.rerenderDT();
                        Swal.fire({
                            title: 'Șters!',
                            text: 'Înregistrare ștersă cu succes.',
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                    },
                    (err: any) => {
                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: err.error.errors.message[0],
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        })
                    }
                );
            }
        });
    }

    changeStatusActive(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul template-ului selectat!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusActiveAction(id);
            }
        });
    }

    changeStatusActiveAction(id: number) {
        this.isLoading = true;
        console.log(id);
        this.componentService.changeStatuseActive(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Functie actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err: any) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusActiveAction(id);
                    }
                });
            }
        );
    }

    /**
     * Load filter options from the new API endpoints
     */
    loadFilterOptions(): void {
        this.componentService.getFilterOptions().subscribe(
            (response: any) => {
                if (response.data) {
                    this.registriesList = response.data.registries || [];
                }
            },
            (error) => {
                console.error('Error loading filter options:', error);
            }
        );
    }

    /**
     * Handle category search input with debouncing
     */
    onCategorySearchChange(event: any): void {
        const searchTerm = event.target.value;

        // Clear previous timeout
        if (this.categorySearchTimeout) {
            clearTimeout(this.categorySearchTimeout);
        }

        // Set new timeout for debouncing
        this.categorySearchTimeout = setTimeout(() => {
            if (searchTerm && searchTerm.length >= 2) {
                this.searchCategories(searchTerm);
            } else {
                this.categorySearchResults = [];
            }
        }, 300); // 300ms debounce
    }

    /**
     * Search categories using the API
     */
    searchCategories(searchTerm: string): void {
        this.componentService.searchCategories(searchTerm).subscribe(
            (response: any) => {
                if (response.data && response.data.results) {
                    this.categorySearchResults = response.data.results;
                }
            },
            (error) => {
                console.error('Error searching categories:', error);
                this.categorySearchResults = [];
            }
        );
    }

    /**
     * Select a category from the search results
     */
    selectCategory(category: any): void {
        this.selectedCategoryId = category.id;
        this.categorySearch = category.text || category.name;
        this.categorySearchResults = [];

        // Optionally apply filters immediately
        // this.applyFilters();
    }

    /**
     * Open clone template dialog
     */
    openCloneDialog(templateData: any): void {
        const dialogRef = this.dialog.open(CloneTemplateDialogComponent, {
            width: '800px',
            maxWidth: '90vw',
            data: {
                templateId: templateData.id,
                templateType: 'oficial', // Default for document templates
                templateName: templateData.name
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.success) {
                this.rerenderDT();
                Swal.fire({
                    title: this.helper.dialogConfig.headers.success,
                    text: 'Template clonat cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    /**
     * Open smart template builder dialog
     */
    openSmartTemplateBuilder(): void {
        const dialogRef = this.dialog.open(SmartTemplateBuilderComponent, {
            width: '95vw',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '90vh',
            disableClose: false,
            autoFocus: false,
            data: {}
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.success) {
                this.rerenderDT();
                Swal.fire({
                    title: this.helper.dialogConfig.headers.success,
                    text: 'Smart template creat cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    /**
     * Open smart template assignments dialog
     */
    openSmartTemplateAssignments(): void {
        const dialogRef = this.dialog.open(SmartTemplateAssignmentComponent, {
            width: '95vw',
            maxWidth: '1600px',
            height: '90vh',
            maxHeight: '90vh',
            disableClose: false,
            autoFocus: false,
            data: {}
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.success) {
                Swal.fire({
                    title: this.helper.dialogConfig.headers.success,
                    text: 'Asignările au fost actualizate cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    /**
     * Open smart template management dialog
     */
    openSmartTemplateManagement(): void {
        const dialogRef = this.dialog.open(SmartTemplateManagementComponent, {
            width: '95vw',
            maxWidth: '1600px',
            height: '90vh',
            maxHeight: '90vh',
            disableClose: false,
            autoFocus: false,
            data: {}
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.success) {
                this.rerenderDT();
                Swal.fire({
                    title: this.helper.dialogConfig.headers.success,
                    text: 'Smart templates au fost actualizate cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }

    /**
     * Open template parameters dialog
     */
    openTemplateParameters(): void {
        const dialogRef = this.dialog.open(TemplateParameterComponent, {
            width: '95vw',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '90vh',
            disableClose: false,
            autoFocus: false,
            data: {}
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.success) {
                Swal.fire({
                    title: this.helper.dialogConfig.headers.success,
                    text: 'Parametrii au fost actualizați cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.dialogConfig.buttonLabels.close,
                });
            }
        });
    }
}
